import { IPhotoModel } from "@common/models";
import Resizer from 'react-image-file-resizer';
import { fileUtil } from '@common/utils';
export interface IAzureImageResizerOptions {
    [propName: string]: any;
    mode?: 'max' | 'pad' | 'crop' | 'stretch' | 'carve',
    height?: number,
    width?: number
}

/**
 * The result type of the promise from 
 */
interface IResizeImageWithPreviewResult {
    fileToSave: File,
    previewDataUrl: string
}

/**
 * Used for react-image-resize
 */
interface IResizeImageWithPreviewOptions {
    saveMaxWidth: number,
    saveMaxHeight: number,
    saveMinWidth?: number,
    saveMinHeight?: number,
    previewMaxWidth: number,
    previewMaxHeight: number,
    previewMinWidth?: number,
    previewMinHeight?: number
}

interface IResizeImageOptions {
    saveMaxWidth: number,
    saveMaxHeight: number,
    saveMinWidth?: number,
    saveMinHeight?: number
}

export class ImageResizerUtil {

    static getAzureImageUrl(blobContainerName?: string, fileName?: string, options?: IAzureImageResizerOptions): string | undefined {
        if (!blobContainerName || !fileName)
            return undefined;

        const encodedFileName = encodeURIComponent(decodeURIComponent(fileName));
        const imageResizerQuery: string | undefined = options ? Object.keys(options).map(key => key + '=' + options[key]).join('&') : undefined;

        if (imageResizerQuery)
            return `/azure/${blobContainerName}/${encodedFileName}?${imageResizerQuery}`;
        return `/azure/${blobContainerName}/${encodedFileName}`;
    }

    static getAzureImageUrlByPhoto(photo?: IPhotoModel, options?: IAzureImageResizerOptions): string | undefined {
        return ImageResizerUtil.getAzureImageUrl(photo?.container, photo?.fileName, options);
    }

    /**
     * This function will resize an image
     * image.
     * @param originalFile The File you want to resize, you can create a File Instance yourself from a blob, or one from a File Input
     * @param options Set the max/min size of the resize file being saved. Images are typically saved at 640 width
     * @returns 
     */
    static ResizeImage(originalFile: File, options: IResizeImageOptions): Promise<File> {
        return new Promise<File>((resolve, reject) => {
            try {
                Resizer.imageFileResizer(
                    originalFile,
                    options.saveMaxWidth,
                    options.saveMaxHeight,
                    "PNG",
                    100,
                    0,
                    (fileToSave: File) => {
                        const adjustedNameToSave = new File([fileToSave], `${fileUtil.getFileNameWithoutExtension(fileToSave)}${'.png'}`);
                        resolve(adjustedNameToSave);
                    },
                    "file",
                    options.saveMinWidth,
                    options.saveMinHeight
                );
            }
            catch (err: any) {
                reject(err);
            }
        });
    }

    /**
     * This function will resize an image twice with the intent of giving you a resized file you want to save and a resized file you want to render for a preview via a dataUrl
     * So you can specify the saveMaxWidth, saveMaxHeight to contrain the demensions of the file you want to use for saving to azure etc, and then previewMaxHeight previewMaxWidth to constraint your preview
     * image.
     * @param originalFile The File you want to resize, you can create a File Instance yourself from a blob, or one from a File Input
     * @param options Set the max size of the resize file being saved, and max size of the preview image 
     * @returns 
     */
    static ResizeImageWithPreview(originalFile: File, options: IResizeImageWithPreviewOptions): Promise<IResizeImageWithPreviewResult> {
        originalFile.name
        return new Promise<IResizeImageWithPreviewResult>((resolve, reject) => {
            try {
                Resizer.imageFileResizer(
                    originalFile,
                    options.saveMaxWidth,
                    options.saveMaxHeight,
                    "PNG",
                    100,
                    0,
                    (fileToSave: File) => {
                        //resize for preview
                        try {
                            Resizer.imageFileResizer(
                                fileToSave,
                                options.previewMaxWidth,
                                options.previewMaxHeight,
                                "PNG",
                                100,
                                0,
                                (previewDataUrl: string) => {
                                    const adjustedNameToSave = new File([fileToSave], `${fileUtil.getFileNameWithoutExtension(fileToSave)}${'.png'}`);
                                    resolve({
                                        fileToSave: adjustedNameToSave,
                                        previewDataUrl: previewDataUrl
                                    });
                                },
                                "base64",
                                options.previewMinWidth,
                                options.previewMinHeight
                            )
                        }
                        catch (err) {
                            reject(err);
                        }
                    },
                    "file",
                    options.saveMinWidth,
                    options.saveMinHeight
                );
            }
            catch (err: any) {
                reject(err);
            }
        });
    }
}
